import req from "@/api/config";
import api from "@/api/v1-jwt/config/api";

/**
 * @typedef {Object} Settings
 * @property {string} logo             - The logo url
 * @property {string} favicon          - The favicon url
 * @property {string} primary_color    - The primary color hex string
 * @property {string} secondary_color  - The secondary color hex string
 * @property {string} accent_color     - The accent color hex string
 * @property {string} marketing_logo     - The marketing image url

/**
 * @returns {Promise<Settings>} A promise to the settings object
 */
export const getSettings = async (userId) => {
  if (!userId) {
    throw new Error("[GET_SETTINGS] User ID missing.");
  }

  const { data } = await api.get(`/platform/settings/${userId}`);
  return {
    ...data,
    logo: data.logo ? `${data.logo}?${Date.now()}` : "",
    marketing_logo: data.marketing_logo
      ? `${data.marketing_logo}?${Date.now()}`
      : "",
  };
};

/**
 * @param {Object} payload
 * @param {string} [payload.logo]             - The logo url
 * @param {string} [payload.marketing_logo]   - The marketing logo url
 * @param {string} [payload.favicon]          - The favicon url
 * @param {string} [payload.primary_color]    - The primary color hex string
 * @param {string} [payload.secondary_color]  - The secondary color hex string
 * @param {string} [payload.accent_color]     - The accent color hex string
 * @throws Will throw an error if status code is >= 400 and < 600
 * @returns {Promise<Settings>} A promise to the settings object
 */
export const updateSettings = async ({ userId, payload }) => {
  if (!userId) {
    throw new Error("[UPDATE_SETTINGS] User ID missing.");
  }

  const { data } = await api.patch(`/platform/settings/${userId}`, payload);

  return data;
};

/**
 * @param {File} file       - The logo file to be uploaded
 * @throws
 * @returns {Promise<void>} - A promise
 */
export const uploadLogo = async ({ userId, file }) => {
  if (!userId) {
    throw new Error("[UPLOAD_LOGO] User ID missing.");
  }

  try {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.post(
      `/settings/files/${userId}/upload/settings`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    await updateSettings({ userId, payload: { logo: data.data } });
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const uploadMarketingLogo = async ({ userId, file }) => {
  if (!userId) {
    throw new Error("[UPLOAD_MARKETING_LOGO] User ID missing.");
  }

  try {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.post(
      `/settings/files/${userId}/upload/client_settings`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    await updateSettings({ userId, payload: { marketing_logo: data.data } });
  } catch (err) {
    console.error(err);
    throw err;
  }
};
